import { inject, Injectable } from '@angular/core';
import { ApprovalState, ApprovalUserRole, ReportType } from '@fdm/api-client';
import { AuthService, CommonUserService, Role } from '@fdm/services';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import { ReportFormStore } from '../report-form.store';
import { POLICY } from './policy-matrix';
import { INITIATOR_TABLES_POLICY } from './creator';
import { ReportFormPage } from '../report-form-control.service';

export const Tabs = [
  'General',
  'Equipment',
  'ApprovalFlow',
  // 'ActionItem'
] as const;
export type Tab = (typeof Tabs)[number];

@Injectable({ providedIn: 'root' })
export class PolicyService {
  private _authService = inject(AuthService);
  private _store = inject(ReportFormStore);
  private _commonUser = inject(CommonUserService);

  public get canRead() {
    const roles = this._authService.getRoles();

    return roles.includes(Role.Readonly);
  }

  public get canCreate$() {
    return this._authService.roles$.pipe(
      map(() => {
        const isAdmin = this._authService.isAdmin();
        const isCreator = this._authService.isCreator();
        return isAdmin || isCreator;
      })
    );
  }

  public get canEditAIOwner$() {
    return combineLatest([
      this._store.general$,
      this._store.reportType$,
      this._store.approvalState$,
      this._commonUser.userEmails$,
    ]).pipe(
      distinctUntilChanged(),
      map(([general, reportType, state, _]) => {
        const initiator = general?.initiator;
        const owner = general?.owner;

        const roles = this._authService.getRoles();

        return roles.some(role => {
          if (role === Role.Admin) {
            return (
              state !== ApprovalState.Cancelled &&
              state !== ApprovalState.Closed &&
              state !== ApprovalState.OutOfService
            );
          }

          if (role === Role.Creator) {
            const isInitiator = initiator ? this._commonUser.isEmailMatchingUser(initiator) : false;
            const isOwner = owner ? this._commonUser.isEmailMatchingUser(owner) : false;

            // Rule 2 — AR: Report Initiator may change AI Owner up to Plan Approved.
            const arEditable = isInitiator && reportType === ReportType.AnomalyReport && (
              state === ApprovalState.Draft ||
              state === ApprovalState.DraftedForReview ||
              state === ApprovalState.PlanConfirmed
            );

            // Rule 1 — TDR/ITR/RTR/IDR/RDR: Report Owner may change AI Owner up to Plan Approved.
            const nonArEditable = isOwner && reportType !== ReportType.AnomalyReport && (
              state === ApprovalState.Draft ||
              state === ApprovalState.DraftedForReview ||
              state === ApprovalState.SubmittedToOpsAdvisor ||
              state === ApprovalState.RIPTScored ||
              state === ApprovalState.RiskEvaluationScored ||
              state === ApprovalState.PlanConfirmed ||
              state === ApprovalState.PlanEndorsed
            );

            return arEditable || nonArEditable;
          }

          return false;
        });
      })
    );
  }

  public get isTARequired$() {
    return combineLatest([
      this._store.general$,
      this._store.reportType$,
      this._store.approvalState$,
      this._commonUser.userEmails$,
    ]).pipe(
      distinctUntilChanged(),
      map(([general, reportType, state, _]) => {
        const initiator = general?.initiator;

        if (!initiator) {
          return false;
        }

        const roles = this._authService.getRoles();

        return roles.some(role => {
          if (role === Role.Creator) {
            const isInitiator = this._commonUser.isEmailMatchingUser(initiator);

            // Initiator can edit TA Required at any stage except Closed.
            return isInitiator && state !== ApprovalState.Closed;
          }

          return false;
        });
      })
    );
  }

  /**
   * Returns an observable that computes canEdit for a SPECIFIC page (tab),
   * independent of which page is currently active in the store.
   * Use this in tab components so that each tab's form enable/disable
   * logic is based on its own step, not the currently viewed page.
   */
  public canEditForPage$(page: ReportFormPage) {
    return combineLatest([
      this._store.general$,
      this._store.reportType$,
      this._store.approvalState$,
      this._store.approvalFlow$,
      this._authService.roles$,
      // Recompute once the current user's emails finish loading; _computeCanEdit
      // resolves the approval role from these emails and returns false until they arrive.
      this._commonUser.userEmails$
    ]).pipe(
      distinctUntilChanged(),
      map(([general, reportType, state, approvalData, roles]) => {
        const tab = page;
        return this._computeCanEdit(general, tab, reportType, state, approvalData, roles);
      })
    );
  }

  public get canEdit$() {
    return combineLatest([
      this._store.general$,
      this._store.page$,
      this._store.reportType$,
      this._store.approvalState$,
      this._store.approvalFlow$,
      this._authService.roles$,
      // Recompute once the current user's emails finish loading; _computeCanEdit
      // resolves the approval role from these emails and returns false until they arrive.
      this._commonUser.userEmails$
    ]).pipe(
      distinctUntilChanged(),
      map(([general, tab, reportType, state, approvalData, roles]) => {
        if (!roles.length) {
          return false;
        }

        if (!state) {
          return true;
        }

        return this._computeCanEdit(general, tab, reportType, state, approvalData, roles);
      })
    );
  }

  /**
   * Can create/edit notes and attachments - available to all users except when Closed.
   */
  public get canCreateNote$() {
    return this._store.approvalState$.pipe(
      distinctUntilChanged(),
      map((state) => state !== ApprovalState.Closed)
    );
  }

  private _computeCanEdit(
    general: any,
    tab: ReportFormPage | null | undefined,
    reportType: ReportType | null | undefined,
    state: ApprovalState | null | undefined,
    approvalData: any,
    roles: Role[]
  ): boolean {
        if (!roles.length) {
          return false;
        }

        if (!state) {
          return true;
        }

        return roles.some(role => {
          if (role === Role.Admin) {
            return true;
          } else if (role === Role.Creator) {
            const approvalUserRole = general && tab ? this._commonUser.getCurrentUserApprovalRoleV2(approvalData, general, tab) : null;

            if (approvalUserRole && reportType && tab) {
              return POLICY?.[role]?.[approvalUserRole]?.[reportType]?.[tab]?.includes(state) ?? false;  
            }
          } else if (role === Role.RIPTRisk && tab === ReportFormPage.RIPT) {
            return state !== ApprovalState.Closed;
          }

          return false;
        });
  }

  public get canEditTable$() {
    return combineLatest([
      this._store.general$,
      this._store.page$,
      this._store.reportType$,
      this._store.approvalState$,
      this._commonUser.userEmails$,
    ]).pipe(
      distinctUntilChanged(),
      map(([general, tab, reportType, state, _]) => {
        const initiator = general?.initiator;

        if (!initiator) {
          return false;
        }

        const roles = this._authService.getRoles();

        return roles.some(role => {
          if (role === Role.Admin) {
            return true;
          } else if (role === Role.Creator) {
            const isInitiator = this._commonUser.isEmailMatchingUser(initiator);

            return isInitiator && reportType && tab && state
              // @ts-ignore
              ? !!INITIATOR_TABLES_POLICY[reportType][tab].includes(state) 
              : false;
          }

          return false;
        });
      })
    );
  }

  public get canEditDamageMechanisms$() {
    return combineLatest([
      this._store.general$,
      this._store.approvalState$,
      this._authService.roles$,
      this._commonUser.userEmails$,
    ]).pipe(
      distinctUntilChanged(),
      map(([general, state, roles]) => {
        if (roles.includes(Role.Admin)) {
          return true;
        }

        return roles.includes(Role.Creator)
          && !!general?.initiator
          && this._commonUser.isEmailMatchingUser(general.initiator)
          && state !== ApprovalState.Closed;
      })
    );
  }
}
